import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1624213012413-fda54df1810f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2NTU0MTk3fDA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const titleLines = ["Eficiencia y", "Seguridad", "Corporativa"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.72, 0.92]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex items-end overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Oficina corporativa moderna" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[2] bg-gradient-to-t from-vnavy via-vnavy/70 to-vnavy/40"
      />
      <div className="absolute inset-0 z-[2] bg-vnavy/50" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-vteal-light" />
          <span className="text-sm uppercase tracking-[0.3em] text-vteal-light font-semibold">
            Grupo Vértice CR · Costa Rica
          </span>
        </motion.div>

        <h1 className="font-serif text-white text-glow font-medium tracking-tighter leading-[1.06] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          {titleLines.map((t, i) => (
            <span key={i} className="reveal-mask pb-[0.22em] -mb-[0.16em]">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-gray-300 leading-relaxed"
        >
          Un equipo multidisciplinario que centraliza <strong className="text-white font-semibold">Recursos Humanos</strong>,{" "}
          <strong className="text-white font-semibold">Asesoría Legal Laboral</strong> y{" "}
          <strong className="text-white font-semibold">Contabilidad</strong> en un solo proveedor de confianza.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-cta-plataformas"
            onClick={() => scrollToId("plataformas")}
            className="px-8 py-4 rounded-full bg-vteal-light text-vnavy font-semibold hover:bg-white transition-colors duration-300"
          >
            Acceso a Sistemas
          </button>
          <button
            data-testid="hero-cta-servicios"
            onClick={() => scrollToId("servicios")}
            className="px-8 py-4 rounded-full border border-white/25 text-white font-semibold hover:border-vteal-light hover:text-vteal-light transition-colors duration-300"
          >
            Nuestros Servicios
          </button>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("nosotros")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-2 text-gray-400"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center mb-6">Scroll</span>
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
